import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireRole?: 'patient' | 'psychologist' | 'admin';
}

export const ProtectedRoute = ({ 
  children, 
  requireAuth = true,
  requireRole
}: ProtectedRouteProps) => {
  const { user, profile, loading, mfaPending } = useAuth();
  const location = useLocation();

  // Mostrar loading enquanto verifica autenticação no AuthProvider
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-[#F5F0EB]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Carregando...</p>
        </div>
      </div>
    );
  }

  // Se requer autenticação e não há usuário, redirecionar para login preservando o state (ex.: intenção de agendamento)
  if (requireAuth && !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname, ...location.state }} />;
  }

  // MFA — sessão em AAL1 com fator TOTP verificado: desafio obrigatório antes de
  // qualquer rota protegida. Precede até o gate de senha (é o segundo fator do
  // próprio login). A rota /verificar-2fa não usa ProtectedRoute.
  if (user && mfaPending && !location.pathname.startsWith('/verificar-2fa')) {
    return <Navigate to="/verificar-2fa" replace />;
  }

  // Tarefa #6 — força troca de senha fraca antes de qualquer rota protegida.
  // Precedência sobre o onboarding: é gate de segurança. Fonte: coluna
  // users.must_reset_password (carregada no profile), setada no signIn e limpa
  // pelo trigger ao trocar a senha. OAuth nunca recebe o flag.
  if (profile?.must_reset_password === true && !location.pathname.startsWith('/atualizar-senha')) {
    return <Navigate to="/atualizar-senha" replace />;
  }

  // Se o usuário está autenticado mas não tem perfil ou user_type
  // (cenário de Google OAuth ou registro incompleto)
  if (user && (!profile || !profile.user_type)) {
    if (location.pathname !== '/onboarding') {
      return <Navigate to="/onboarding" replace />;
    }
  }

  // Forçar onboarding apenas para psicólogos que ainda não o concluíram.
  // Fonte única: users.onboarding_completed (coluna), carregada no profile.
  // O loader acima (loading) garante profile já resolvido aqui — sem fast-path de localStorage.
  if (profile?.user_type === 'psychologist' && !location.pathname.startsWith('/onboarding')) {
    if (profile.onboarding_completed !== true) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  // Se requer role específico e o user_type não corresponde, redirecionar para o dashboard correto
  if (requireRole && profile && profile.user_type !== requireRole) {
    const correctDashboard =
      profile.user_type === 'admin'
        ? '/admin/dashboard'
        : profile.user_type === 'psychologist'
          ? '/psychologist-dashboard'
          : '/dashboard';
    return <Navigate to={correctDashboard} replace />;
  }

  // Tudo OK, renderizar o conteúdo protegido
  return <>{children}</>;
};
